/**
 * ===========================================================================
 * LOBBY — presence, matchmaking-by-map, duel challenges
 * ===========================================================================
 *
 * A second, much smaller socket alongside `net` (`src/net/index.js`). `net`
 * is the live match — this is everywhere BEFORE that: who else is online
 * right now, standing in a queue for a map, and challenging someone to a
 * duel. It talks to `GET /lobby` on the same server, over the same
 * `NetSocket` transport `net` uses.
 *
 * A client holds this connection only while it is browsing the online menu.
 * The moment matchmaking seals a group or a challenge is accepted, the
 * server hands back a `match_id` — this system emits `lobby:match-ready` with
 * it, `mainmenu.js` calls `net.connectToMatch(matchId)`, and this socket
 * disconnects. That is also why duels can only ever target someone shown
 * here: once a player's match starts, they leave this roster.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API — `const lobby = ctx.get('lobby')`
 * ---------------------------------------------------------------------------
 *   lobby.connect() / disconnect()
 *   lobby.roster                    last known [{id, name}, ...]
 *   lobby.queue(mode, mapId)        stand in the queue for a (mode, map)
 *   lobby.cancelQueue()
 *   lobby.challenge(targetId)       ask to duel a specific online player
 *   lobby.acceptChallenge(fromId, mapId)   accept — YOU choose the map
 *   lobby.declineChallenge(fromId)
 *
 * EVENTS (via `ctx.events`)
 *   lobby:roster          { players: [{id,name}] }
 *   lobby:queue           { mode, map, players, required } | null (cancelled)
 *   lobby:challenge       { fromId, fromName }        — someone challenged you
 *   lobby:challenge-sent  { targetId }
 *   lobby:challenge-declined  { targetId }
 *   lobby:challenge-expired   { targetId }
 *   lobby:match-ready     { matchId, mode, map }
 *   lobby:error           { reason }
 */

import { NetSocket } from './socket.js';

function defaultLobbyUrl() {
  const { protocol, hostname, host } = location;
  if (protocol === 'https:') return `wss://${host}/mp/lobby`;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'ws://127.0.0.1:7801/lobby';
  return `ws://${hostname}:7801/lobby`;
}

export class LobbySystem {
  static id = 'lobby';

  async init(ctx) {
    this.ctx = ctx;

    const params = new URLSearchParams(location.search);
    const capture = params.get('capture') === '1' || ctx.config.deterministic;
    this.enabled = !capture && params.get('mp') !== '0';

    this.id = 0;
    this.connected = false;
    this.roster = [];
    this.queueState = null;

    if (!this.enabled) return;

    // Same identity `net` resolves — a shared callsign whether you queue,
    // duel, or end up in a match.
    const wanted = params.get('name') ?? localStorage.getItem('cod:name') ?? '';
    this.name = (wanted || `OPERATOR${100 + Math.floor(Math.random() * 900)}`).toUpperCase().slice(0, 16);

    this.url = params.get('lobby') ?? defaultLobbyUrl();
    this.socket = this._createSocket();
  }

  _createSocket() {
    return new NetSocket({
      url: this.url,
      onOpen: () => this._onOpen(),
      onClose: () => this._onClose(),
      onMessage: (msg) => this._onMessage(msg),
    });
  }

  connect() {
    if (!this.enabled || !this.socket) return false;
    // `close()` sets `_stopped`, and a stopped socket never reconnects on its
    // own — the same pattern `net.setSessionMode` uses for its own socket.
    if (this.socket._stopped) this.socket = this._createSocket();
    this.socket.connect();
    return true;
  }

  disconnect() {
    if (!this.socket) return;
    this.connected = false;
    this.roster = [];
    this.queueState = null;
    this.socket.close();
  }

  queue(mode, mapId) {
    if (!this.socket) return false;
    // The menu deliberately calls connect() and queue() back-to-back. A real
    // WebSocket will still be CONNECTING at that point, so send() used to drop
    // every first matchmaking request and leave the UI searching forever.
    // NetSocket.queue() holds this one command until onopen; _onOpen() sends
    // Hello first, then NetSocket flushes this request in protocol order.
    return this.socket.queue({ t: 'queue_map', mode, map: mapId });
  }

  cancelQueue() {
    if (!this.socket) return false;
    // Queue cancellation too: if the player backs out while the initial
    // connection is opening, Hello -> QueueMap -> CancelQueue is flushed in
    // order and cannot leave a ghost queue entry on the server.
    return this.socket.queue({ t: 'cancel_queue' });
  }

  challenge(targetId) {
    if (!this.connected) return false;
    return this.socket.send({ t: 'challenge', target: targetId });
  }

  acceptChallenge(fromId, mapId) {
    if (!this.connected) return false;
    return this.socket.send({ t: 'challenge_accept', from: fromId, map: mapId });
  }

  declineChallenge(fromId) {
    if (!this.connected) return false;
    return this.socket.send({ t: 'challenge_decline', from: fromId });
  }

  _onOpen() {
    this.socket.send({ t: 'hello', name: this.name });
  }

  _onClose() {
    this.connected = false;
    this.roster = [];
    this.ctx?.events.emit('lobby:roster', { players: [] });
  }

  _onMessage(m) {
    switch (m?.t) {
      case 'hello':
        this.id = m.id ?? 0;
        this.connected = true;
        break;
      case 'roster':
        this.roster = m.players ?? [];
        this.ctx?.events.emit('lobby:roster', { players: this.roster });
        break;
      case 'queue_status':
        this.queueState = { mode: m.mode, map: m.map, players: m.players ?? 0, required: m.required ?? 0 };
        this.ctx?.events.emit('lobby:queue', this.queueState);
        break;
      case 'queue_cancelled':
        this.queueState = null;
        this.ctx?.events.emit('lobby:queue', null);
        break;
      case 'match_ready':
        this.queueState = null;
        this.ctx?.events.emit('lobby:match-ready', { matchId: m.match_id, mode: m.mode, map: m.map });
        break;
      case 'challenge_received':
        this.ctx?.events.emit('lobby:challenge', { fromId: m.from_id, fromName: m.from_name });
        break;
      case 'challenge_sent':
        this.ctx?.events.emit('lobby:challenge-sent', { targetId: m.target_id });
        break;
      case 'challenge_declined':
        this.ctx?.events.emit('lobby:challenge-declined', { targetId: m.target_id });
        break;
      case 'challenge_expired':
        this.ctx?.events.emit('lobby:challenge-expired', { targetId: m.target_id });
        break;
      case 'error':
        this.ctx?.events.emit('lobby:error', { reason: m.reason });
        break;
      default:
        break;
    }
  }

  update(dt) {
    if (!this.enabled || !this.socket) return;
    this.socket.update(dt);
  }
}
